import { Inject, Injectable } from '@angular/core';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ISettings, SETTINGS, WINDOW } from '@lsq-frontend/angular-shared/settings';
import { Guid } from '@lsq-frontend/remote-services/enterprise/common';
import { filter } from 'rxjs/operators';
import { AbstractAnalyticsService } from './abstract-analytics.service';
import { AnalyticsEvent } from './analytics.constants';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
    constructor(
        @Inject(AbstractAnalyticsService) private _subAnalyticsServices: AbstractAnalyticsService[],
        private _userService: UserService,
        @Inject(SETTINGS) private _settings: ISettings,
        @Inject(WINDOW) private _window: Window & typeof globalThis
    ) {
        SingletonServiceGuard.register('AnalyticsService');
        this.initalize();
    }

    public initalize(): void {
        this._userService.user$.pipe(filter((user) => !!user)).subscribe((user) => {
            this._subAnalyticsServices?.forEach((analyticsService) => {
                analyticsService.initalize(user.isImpersonating && !this._settings.isInternalApp);

                this._window.addEventListener('beforeunload', () => {
                    analyticsService.triggerSimpleEvent(AnalyticsEvent.LeavePage);
                });
            });

            if (this._settings.isInternalApp) {
                this.setUserData(
                    `${user.firstName} ${user.lastName}`,
                    user.emailAddress,
                    user.userKey,
                    user.isImpersonating
                );
            } else if (user.currentCompany && user.currentCompany.name) {
                this.setCompanyData(
                    user.currentCompany.name,
                    user.emailAddress,
                    user.userKey,
                    user.firstName,
                    user.lastName,
                    user.isImpersonating
                );
            }
        });

        this._userService.logout$.subscribe(() => {
            this.clearIdentifiersData();
        });
    }

    public setUserData(userName: string, email: string, userKey: Guid, isAdmin: boolean): void {
        this._subAnalyticsServices?.forEach((analyticsService) => {
            analyticsService.setUserData(userName, email, userKey, isAdmin);
        });
    }

    public clearIdentifiersData(): void {
        this._subAnalyticsServices?.forEach((analyticsService) => {
            analyticsService.clearIdentifiersData();
        });
    }

    public setCompanyData(
        companyName: string,
        email: string,
        userKey: Guid,
        firstName: string,
        lastName: string,
        isAdmin: boolean
    ): void {
        this._subAnalyticsServices?.forEach((analyticsService) => {
            analyticsService.setCompanyData(
                companyName,
                email,
                userKey,
                firstName,
                lastName,
                isAdmin
            );
        });
    }

    public setPageView(path: string, title: string): void {
        this._subAnalyticsServices?.forEach((analyticsService) => {
            analyticsService.setPageView(path, title);
        });
    }

    public triggerSimpleEvent(eventName: string): void {
        this._subAnalyticsServices?.forEach((analyticsService) => {
            analyticsService.triggerSimpleEvent(eventName);
        });
    }

    public triggerEventWithPayload(eventName: string, data: { [key: string]: any }): void {
        this._subAnalyticsServices?.forEach((analyticsService) => {
            analyticsService.triggerEventWithPayload(eventName, data);
        });
    }

    public triggerSingleDateFilterEvent(reportName: string, date: Date): void {
        const dayOffset = this._getDateOffset(date);
        this._subAnalyticsServices?.forEach((analyticsService) => {
            analyticsService.triggerSingleDateFilterEvent(reportName, dayOffset);
        });
    }

    public triggerDateRangeFilterEvent(reportName: string, startDate: Date, endDate: Date): void {
        const startDayOffset = this._getDateOffset(startDate);
        const endDayOffset = this._getDateOffset(endDate);
        this._subAnalyticsServices?.forEach((analyticsService) => {
            analyticsService.triggerDateRangeFilterEvent(reportName, startDayOffset, endDayOffset);
        });
    }

    private _getDateOffset(date: Date): number {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const oneDay = 1000 * 60 * 60 * 24;
        const todayMillis = today.getTime();
        const dateMillis = date.getTime();
        const differenceMillis = dateMillis - todayMillis;
        return Math.abs(Math.round(differenceMillis / oneDay));
    }
}
